import { FormEvent, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import type { Session } from "@supabase/supabase-js";
import { supabase } from "../lib/supabase";
import { useStaffRole } from "../lib/admin";
import { iconSvg } from "../lib/icons";
import { fijiToday } from "../lib/history";
import {
  COLUMNS, PRESET_LABEL, REPORT_LABEL, REPORT_MAX_DAYS, daysBetween, download, fmtMin, presetRange, runReport, toCsv,
  type Preset, type ReportResult, type ReportType,
} from "../lib/reports";
import { RouteLoader, useLoaderPhase } from "../ui/RouteLoader";
import ReportPrint from "./reports/ReportPrint";
import AppFooter from "../ui/AppFooter";
import CompanyBadge from "../ui/CompanyBadge";
import TeamPresence from "../ui/TeamPresence";
import AdminLink from "../ui/AdminLink";
import UserMenu from "../ui/UserMenu";

type Obj = { id: string; name: string; icon: string | null; org: string | null; group: string | null };

export default function ReportsPage({ session }: { session: Session }) {
  const staff = useStaffRole(session.user.id);
  const [objs, setObjs] = useState<Obj[]>([]);
  const [picked, setPicked] = useState<Set<string>>(new Set());
  const [filter, setFilter] = useState("");
  const [type, setType] = useState<ReportType>("daily");
  const [preset, setPreset] = useState<Preset>("last7");
  const [[fromDay, toDay], setRange] = useState<[string, string]>(presetRange("last7"));
  const [limit, setLimit] = useState(80);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [res, setRes] = useState<ReportResult | null>(null);
  const [resDays, setResDays] = useState<[string, string]>([fromDay, toDay]);
  const [resIds, setResIds] = useState<string[]>([]);
  const phase = useLoaderPhase(busy, !!err, "report");

  useEffect(() => {
    supabase.from("devices").select("id, name, icon, status, organisations(name), device_groups(name)").neq("status", "retired").order("name")
      .then(({ data }) => {
        const list = (data ?? []).map((d: any) => ({ id: d.id, name: d.name, icon: d.icon, org: d.organisations?.name ?? null, group: d.device_groups?.name ?? null }));
        setObjs(list);
        if (list.length === 1) setPicked(new Set([list[0].id]));
      });
  }, []);

  const shown = useMemo(() => {
    const q = filter.trim().toLowerCase();
    return objs.filter((o) => !q || o.name.toLowerCase().includes(q) || o.org?.toLowerCase().includes(q));
  }, [objs, filter]);
  const multiOrg = new Set(objs.map((o) => o.org)).size > 1;

  const days = daysBetween(fromDay, toDay);
  const tooLong = days > REPORT_MAX_DAYS[type];

  function choosePreset(p: Preset) {
    setPreset(p);
    if (p !== "custom") setRange(presetRange(p));
  }
  function toggle(id: string) {
    const n = new Set(picked);
    n.has(id) ? n.delete(id) : n.add(id);
    setPicked(n);
  }

  async function run(e?: FormEvent) {
    e?.preventDefault();
    if (!picked.size) return setErr("Choose at least one object.");
    if (fromDay > toDay) return setErr("The start date is after the end date.");
    if (tooLong) return setErr(`${REPORT_LABEL[type]} reports are limited to ${REPORT_MAX_DAYS[type]} days.`);
    setBusy(true); setErr(null);
    try { setRes(await runReport(type, [...picked], fromDay, toDay, type === "speeding" ? limit : undefined)); setResDays([fromDay, toDay]); setResIds([...picked]); }
    catch (e: any) { setErr(e.message); setRes(null); }
    finally { setBusy(false); }
  }

  const period = fromDay === toDay ? fromDay : `${fromDay} to ${toDay}`;
  // V3.3.0: default file name = report type, object(s) and date(s), e.g. TrackingFiji_Daily-summary_ST-901-Test-1_2026-10-10
  const fileBase = reportFileName(REPORT_LABEL[res?.type ?? type], resIds.map((id) => objs.find((o) => o.id === id)?.name ?? "").filter(Boolean), resDays[0], resDays[1]);
  function printPdf() {
    const before = document.title;
    const restore = () => { document.title = before; window.removeEventListener("afterprint", restore); };
    document.title = fileBase;           // browsers use the page title as the suggested PDF name
    window.addEventListener("afterprint", restore);
    window.print();
    setTimeout(restore, 1000);
  }

  return (
    <div className="admin reports">
      <header className="topbar no-print">
        <img src="/logo.png" alt="Tracking Fiji" className="topbar-logo" />
        <span className="beta">Reports</span>
        <div className="topbar-spacer" />
        <CompanyBadge userId={session.user.id} />
        <TeamPresence />
        <Link className="btn-ghost btn-map" to="/map">← Map</Link>
        <AdminLink role={staff} />
        <UserMenu email={session.user.email} />
      </header>

      <main className="amain rep-layout">
        <form className="rep-controls no-print" onSubmit={run}>
          <h1>Reports</h1>
          <div className="seg">
            {(Object.keys(REPORT_LABEL) as ReportType[]).map((t) => (
              <button type="button" key={t} className={type === t ? "on" : ""} onClick={() => { setType(t); setRes(null); }}>{REPORT_LABEL[t]}</button>
            ))}
          </div>

          <div className="rep-block">
            <div className="rep-label">Period <span className="sub">(Fiji time)</span></div>
            <div className="chips">
              {(Object.keys(PRESET_LABEL) as Preset[]).map((p) => (
                <button type="button" key={p} className={preset === p ? "on" : ""} onClick={() => choosePreset(p)}>{PRESET_LABEL[p]}</button>
              ))}
            </div>
            <div className="date-pair">
              <input type="date" value={fromDay} max={fijiToday()} onChange={(e) => { setPreset("custom"); setRange([e.target.value, toDay]); }} />
              <span>to</span>
              <input type="date" value={toDay} max={fijiToday()} onChange={(e) => { setPreset("custom"); setRange([fromDay, e.target.value]); }} />
            </div>
            {tooLong && <div className="sub warn">Max {REPORT_MAX_DAYS[type]} days for this report — you've picked {days}.</div>}
          </div>

          {type === "speeding" && (
            <div className="rep-block">
              <label className="rep-label" htmlFor="lim">Speed limit</label>
              <div className="date-pair"><input id="lim" type="number" min={10} max={200} value={limit} onChange={(e) => setLimit(Number(e.target.value))} /><span>km/h</span></div>
            </div>
          )}

          <div className="rep-block grow">
            <div className="rep-label">
              Objects <span className="sub">{picked.size} of {objs.length}</span>
              <button type="button" className="linkish" onClick={() => setPicked(picked.size === objs.length ? new Set() : new Set(objs.map((o) => o.id)))}>
                {picked.size === objs.length ? "None" : "All"}
              </button>
            </div>
            {objs.length > 8 && <input className="asearch" placeholder="Filter…" value={filter} onChange={(e) => setFilter(e.target.value)} />}
            <ul className="obj-pick">
              {shown.map((o) => (
                <li key={o.id}>
                  <label>
                    <input type="checkbox" checked={picked.has(o.id)} onChange={() => toggle(o.id)} />
                    <span className="ticon" dangerouslySetInnerHTML={{ __html: iconSvg(o.icon, 15) }} />
                    <span>{o.name}{multiOrg && o.org && <span className="sub"> · {o.org}</span>}</span>
                  </label>
                </li>
              ))}
              {!objs.length && <li className="sub">No objects available.</li>}
            </ul>
          </div>

          {err && <div className="error">{err}</div>}
          <button className="btn-primary" disabled={busy}>{busy ? "Running…" : "Run report"}</button>
        </form>

        <section className="rep-out">
          {!res && !busy && !phase && <div className="rep-empty no-print">Choose a report, period and objects, then press <b>Run report</b>.</div>}
          {phase ? (
            <div className="rep-loader" style={{ display: "grid", placeItems: "center", minHeight: 260 }}>
              <RouteLoader phase={phase} kind="report" size="inline"
                sub={phase === "report" ? (type === "stops" ? "Processing stops" : type === "speeding" ? "Checking speeds" : type === "daily" ? "Processing days" : undefined) : undefined} />
            </div>
          ) : busy ? <div className="rep-empty">Building the report…</div> : null}
          {res && !busy && !phase && (
            <>
              <div className="atoolbar no-print">
                <h2 className="rep-title">{REPORT_LABEL[res.type]}{res.type === "speeding" && ` over ${res.limit} km/h`}</h2>
                <span className="sub">{period} · {res.rows.length} rows</span>
                <div className="rep-actions">
                  <button className="btn-ghost" disabled={busy} onClick={() => download(`${fileBase}.csv`, toCsv(res.type, res.rows))}>Download CSV</button>
                  <button className="btn-ghost" disabled={busy} onClick={printPdf}>Print / PDF</button>
                </div>
              </div>
              <Totals r={res} />
              <div className="atable-wrap">
                <table className="atable static">
                  <thead><tr>{COLUMNS[res.type].map((c) => <th key={c.key} className={c.num ? "num" : ""}>{c.label}</th>)}</tr></thead>
                  <tbody>
                    {res.rows.map((r, i) => (
                      <tr key={i}>{COLUMNS[res.type].map((c) => <td key={c.key} className={c.num ? "num mono" : c.key === "place" || c.key === "from" || c.key === "to" ? "mono" : ""}>{c.value(r)}</td>)}</tr>
                    ))}
                    {!res.rows.length && <tr className="empty"><td colSpan={COLUMNS[res.type].length}>Nothing found for this period.</td></tr>}
                  </tbody>
                </table>
              </div>
            </>
          )}
        </section>
      </main>
      <div className="no-print"><AppFooter /></div>
      {res && !busy && <ReportPrint res={res} objs={objs} fromDay={resDays[0]} toDay={resDays[1]} />}
    </div>
  );
}

function Totals({ r }: { r: ReportResult }) {
  const t = r.totals;
  const tiles: [string, string][] =
    r.type === "stops" ? [["Stops", String(t.stops)], ["Time stopped", fmtMin(t.stoppedMin)]]
    : r.type === "speeding" ? [["Speeding events", String(t.events)], ["Top speed", `${t.maxKmh} km/h`]]
    : [["Distance", `${t.distanceKm} km`], ["Trips", String(t.trips)], ["Driving time", fmtMin(t.drivingMin)], ["Top speed", `${t.maxKmh} km/h`]];
  return (
    <div className="rep-tiles">
      {tiles.map(([k, v]) => <div key={k}><b className="mono">{v}</b><span>{k}</span></div>)}
    </div>
  );
}

const slug = (s: string) => s.normalize("NFKD").replace(/[^\w\s-]/g, "").trim().replace(/[\s_]+/g, "-").replace(/-+/g, "-");
export function reportFileName(label: string, names: string[], from: string, to: string) {
  const who = names.length === 0 ? "" : names.length <= 2 ? names.map(slug).join("+") : `${names.length}-objects`;
  const when = from === to ? from : `${from}_to_${to}`;
  return ["TrackingFiji", slug(label), who, when].filter(Boolean).join("_").slice(0, 150);
}
